/* eslint-disable */
import Vue from 'vue'

import apollo from '@/graphql'

import AuditDetailsQuery from '@/graphql/audit/AuditDetailsQuery.gql'
import ReviewAllAuditProducts from '@/graphql/audit/ReviewAllAuditProducts.gql'
import SavePogProductCoords from '@/graphql/pog_product/SavePogProductCoords.gql'
import TakeLaneSidePicture from '@/graphql/lane_side/TakeLaneSidePicture.gql'
import SaveNextAuditDate from '@/graphql/audit/SaveNextAuditDate.gql'

// initial state
const state = {
  auditDetails: {},
  /**
   * `auditIssuesId` used by AuditReview.js
   * as a flag to select a particular product on page load.
   */
  auditIssuesId: null,
  auditSelector: {},
  inMemoryAudit: {},
  lastAuditDetailsRequestTime: null,
  pendingAuditSaves: 0,
}

// getters
const getters = {
  auditDetails: st => st.auditDetails,

  inMemoryAudit: st => st.inMemoryAudit,

  editableAuditFields2: (st, { auditSelector, inMemoryAudit }) => {
    return !inMemoryAudit.pogs
      ? null
      : inMemoryAudit.pogs[auditSelector.pogIx].products[auditSelector.productIx]
  },

  auditProducts2: (st, { auditSelector, inMemoryAudit }) => {
    return !inMemoryAudit.pogs ? null : inMemoryAudit.pogs[auditSelector.pogIx].products
  },

  auditCrop2: (st, { auditSelector, inMemoryAudit }) => {
    return !inMemoryAudit.pogs ? null : inMemoryAudit.pogs[auditSelector.pogIx].crop
  },

  auditImages: (st, { auditDetails, auditPogRack }) =>
    !auditDetails.images
      ? null
      : auditDetails.images.filter(image => image.laneSide.id === auditPogRack.laneSide.id),

  auditIssueEmptyTypes: () => [
    { id: 1, name: 'Not on Rack' },
    { id: 2, name: 'Empty Box' },
    { id: 4, name: 'Empty Box & Wrong Position' },
  ],

  // TODO combine w/ auditProductCoordMap
  auditIssueMap: (st, { auditDetails }) => {
    const map = {}

    if (auditDetails.issues) {
      auditDetails.issues.forEach(coord => {
        map[coord.pogsProductsId] = map[coord.pogsProductsId] || {}
        map[coord.pogsProductsId][coord.pogsRacksId] = coord
      })
    }
    return map
  },

  auditIssueWrongPlaceTypes: () => [
    { id: 1, name: 'Not on Rack' },
    { id: 3, name: 'Wrong Position' },
    { id: 4, name: 'Empty Box & Wrong Position' },
    { id: 6, name: 'Product Obstructed & Wrong Position' },
  ],

  auditPogProduct: (st, { auditProducts }) =>
    !auditProducts ? null : auditProducts[st.auditSelector.productIx],

  auditPogRack: st =>
    !st.auditDetails.pogsRacks ? null : st.auditDetails.pogsRacks[st.auditSelector.pogIx],

  auditPreviousImage: (st, { auditDetails, auditPogRack }) =>
    !auditDetails.previousImages
      ? null
      : auditDetails.previousImages.filter(
          image => image.laneSide.id === auditPogRack.laneSide.id,
        )[0] || {},

  /**
   * create this map (and the issue map)
   * so that the each pogProduct in the current list
   * can be linked to its corresponding productCoord and issue
   */
  auditProductCoordMap: (st, { auditDetails }) => {
    const map = {}
    if (auditDetails.productCoords) {
      auditDetails.productCoords.forEach(coord => {
        map[coord.pogsProductsId] = map[coord.pogsProductsId] || {}
        map[coord.pogsProductsId][coord.pogsRacksId] = coord
      })
    }
    return map
  },

  /**
   * Using the map created auditProductCoordMap (and the corresponding issue map),
   * link each pogProduct to its respective productCoord and issue.
   */
  auditProducts: (st, { auditDetails, auditIssueMap, auditPogRack, auditProductCoordMap }) =>
    !auditDetails.pogsProducts
      ? null
      : auditDetails.pogsProducts
          .filter(pogProduct => pogProduct.pogsRacksId === auditPogRack.id)
          .map(pogProduct => {
            const coordMap = auditProductCoordMap[pogProduct.pogsProductsId] || {}
            const coord = coordMap[pogProduct.pogsRacksId] || {}

            const issueMap = auditIssueMap[pogProduct.pogsProductsId] || {}
            const issue = issueMap[pogProduct.pogsRacksId] || { coord: {} }

            return Object.assign({ coord, issue }, pogProduct)
          }),

  auditRackCrop: (st, { auditDetails, auditPogRack }) =>
    !auditDetails.rackCrops
      ? null
      : auditDetails.rackCrops.filter(crop => crop.rack.id === auditPogRack.rack.id).concat({})[0],

  auditSelector: (st, { auditDetails }) => {
    /**
     * if `auditIssuesId` flag is set,
     * use it to select a product.
     */
    if (state.auditIssuesId) {
      const issue = auditDetails.issues.find(iss => iss.id === state.auditIssuesId)

      // this issue was just removed
      if (!issue) return st.auditSelector

      const pogIx = auditDetails.pogsRacks.findIndex(pr => pr.id === issue.pogsRacksId)

      const productIx = auditDetails.pogsProducts
        .filter(pp => pp.pogsRacksId === issue.pogsRacksId)
        .findIndex(pp => pp.pogsProductsId === issue.pogsProductsId)

      return Object.assign(st.auditSelector, { pogIx, productIx })
    }

    return st.auditSelector
  },

  isAuditImagePending: (st, { auditDetails, auditPogRack }) =>
    !auditDetails.pendingImages
      ? null
      : auditDetails.pendingImages.filter(
          image => image.args.indexOf(`"lane_side":${auditPogRack.laneSide.id}`) > -1,
        ).length,

  isAuditIssueWrongPlace: (st, { auditIssueWrongPlaceTypes, editableAuditFields2 }) => {
    if(!editableAuditFields2) return null
    const typesId = editableAuditFields2.issue.typesId
    return (
      typesId !== 1 && auditIssueWrongPlaceTypes.filter(type => type.id === typesId).length // no box for this type
    )
  },

  isAuditSaving: st => st.pendingAuditSaves > 0,

  reviewedAuditProducts: (st, { inMemoryAudit }) => {
    return !inMemoryAudit.pogs
      ? null
      : inMemoryAudit.pogs.reduce((value, pog) =>
        (value + pog.products.filter(product => product.coords.isReviewed).length), 0)
  },
}

// mutations
const mutations = {
  editAuditIssue(st, { id }) {
    state.auditIssuesId = id
  },

  decPogIndex(st, { $store }) {
    if (st.auditSelector.pogIx > 0) {
      $store.commit('setPogIndex', {
        ix: st.auditSelector.pogIx - 1,
        $store,
      })
    }
  },

  incPogIndex(st, { $store }) {
    if (st.auditSelector.pogIx + 1 < state.auditDetails.pogsRacks.length) {
      $store.commit('setPogIndex', {
        ix: st.auditSelector.pogIx + 1,
        $store,
      })
    }
  },

  setAuditDetails(st, { auditDetails, hasPermission }) {
    const isVendor = hasPermission('role:vendor')

    state.auditDetails = auditDetails

    // only update editable fields with the very most recent request data
    // this is to avoid getting back to the current state in a piece-meal fashion
    // if (state.lastAuditDetailsRequestTime !== auditDetails.requestTime) return

    // TODO intelligently over-write all non-dirty objects
    // If anything is dirty, don't do ANY overwriting.
    // if (st.inMemoryAudit.pogs) {
    //   // no dirty-ness!
    //   for (let i = 0; i < st.inMemoryAudit.pogs.length; i += 1) {
    //     if (st.inMemoryAudit.pogs[i].isDirty) return
    //     for (let j = 0; j < st.inMemoryAudit.pogs[i].products.length; j += 1) {
    //       if (st.inMemoryAudit.pogs[i].products[j].isDirty) return
    //     }
    //   }
    // }

    /**
     * Keep this linear time complexity!
     * Only iterate once through
     * pogsProducts, productCoords, issues, images, and pogsRacks
     */
    const start = Date.now()
    // by pogsRacksId
    const coords = {}
    auditDetails.productCoords.forEach(coord => {
      coords[coord.pogsProductsId] = coords[coord.pogsProductsId] || {}
      coords[coord.pogsProductsId][coord.pogsRacksId] = coord
    })

    const issues = {}
    auditDetails.issues.forEach(issue => {
      issues[issue.pogsProductsId] = issues[issue.pogsProductsId] || {}
      issues[issue.pogsProductsId][issue.pogsRacksId] = issue
    })

    /**
     * PogsProducts will use coords and issues
     */
    const pogsProducts = {}
    auditDetails.pogsProducts.forEach(pogProduct => {
      const coordMap = coords[pogProduct.pogsProductsId] || {}
      const coord = coordMap[pogProduct.pogsRacksId] || {}

      const issueMap = issues[pogProduct.pogsProductsId] || {}
      const issue = issueMap[pogProduct.pogsRacksId] || { coord: {} }

      pogsProducts[pogProduct.pogsRacksId] = pogsProducts[pogProduct.pogsRacksId] || []
      pogsProducts[pogProduct.pogsRacksId].push({
        coords: {
          // isReviewable could be the non-null value `false`
          isReviewable: coord.isReviewable === undefined ? true : coord.isReviewable,
          isReviewed: coord.isReviewed,
          height: coord.height || 0.075,
          width: coord.width || 0.05,
          x: coord.x || 0,
          y: coord.y || 0,
        },
        id: pogProduct.pogsProductsId,
        issue: {
          height: issue.coord.height || 0.075,
          // so that we can still know this when user is a vendor
          typesId: issue.typesId,
          width: issue.coord.width || 0.05,
          x: issue.coord.x || 0,
          y: issue.coord.y || 0,
        },
        pogsRacksId: pogProduct.pogsRacksId,
        position: pogProduct.position,
        product: pogProduct.product,
        shelf: pogProduct.shelf,
      })
    })
    // Vue.set() is required for page updating to work
    Vue.set(
      state.inMemoryAudit,
      'pogs',
      auditDetails.pogsRacks.map(pogRack => ({
        crop: Object.assign(
          {},
          auditDetails.rackCrops.find(crop => crop.rack.id === pogRack.rack.id),
        ),
        id: pogRack.id,
        image: auditDetails.images.find(image => image.laneSide.id === pogRack.laneSide.id),
        products: pogsProducts[pogRack.id],
      })),
    )

    console.log(
      'auditDetails: building this map took',
      Date.now() - start,
      Date.now() - state.lastAuditDetailsRequestTime,
    )
  },

  setPogIndex(st, { ix }) {
    Object.assign(state.auditSelector, {
      imageIx: 0,
      pogIx: ix,
      productIx: 0,
    })
  },

  setProductIndex(st, { ix }) {
    state.auditSelector.productIx = ix
  },
}

// actions
const actions = {
  initializeAuditIssueCoords({
    getters: {
      editableAuditFields2,
    },
  }) {
    const fields = editableAuditFields2

    Object.assign(fields.issue, {
      x: fields.coords.x + fields.coords.width,
      y: fields.coords.y,
      width: fields.coords.width,
      height: fields.coords.height,
    })
  },

  initializeProductCoords({
    getters: {
      auditProducts2,
      auditRackCrop,
      editableAuditFields2,
    },
  }) {
    const products = auditProducts2
    const box = editableAuditFields2

    const boxToLeft = products.find((product) => {
      return product.shelf === box.shelf
        && product.position === (box.position - 1)
    })

    if (boxToLeft) {
      Object.assign(box.coords, {
        x: boxToLeft.coords.x + boxToLeft.coords.width,
        y: boxToLeft.coords.y,
        width: boxToLeft.coords.width,
        height: boxToLeft.coords.height,
      })
      return
    }

    const boxAbove = products.find((product) => {
      return product.position === box.position
        && product.shelf === (box.shelf + 1)
    })

    if (boxAbove) {
      Object.assign(box.coords, {
        x: boxAbove.coords.x,
        y: boxAbove.coords.y + boxAbove.coords.height,
        width: boxAbove.coords.width,
        height: boxAbove.coords.height,
      })
      return
    }

    Object.assign(box.coords, {
      x: -auditRackCrop.tx || 0,
      y: -auditRackCrop.ty || 0,
    })
  },

  // eslint-disable-next-line
  async loadAuditDetails({ commit, getters }, { doResetSelector, id, initialLoad }) {
    const hasPermission = getters['Auth/hasPermission']
    if (initialLoad) {
      Object.assign(state, {
        auditDetails: {},
        auditSelector: {},
        inMemoryAudit: {},
        lastAuditDetailsRequestTime: null,
        pendingAuditSaves: 0,
      })
    }

    if (doResetSelector) {
      state.auditSelector = {
        imageIx: 0,
        pogIx: 0,
        productIx: 0,
      }
    }

    state.lastAuditDetailsRequestTime = Date.now()

    const {
      data: { auditDetails },
    } = await apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: AuditDetailsQuery,
      variables: { id: +id, requestTime: state.lastAuditDetailsRequestTime },
    })

    commit('setAuditDetails', { auditDetails, hasPermission })
  },

  refreshAuditImage(
    {
      dispatch,
      getters: { auditDetails, auditPogRack },
    },
  ) {
    return apollo.defaultClient
      .mutate({
        mutation: TakeLaneSidePicture,
        variables: {
          auditsId: auditDetails.id,
          laneSidesId: auditPogRack.laneSide.id,
        },
      })
      .then(() => {
        dispatch('loadAuditDetails', { id: +auditDetails.id })
      })
  },

  reviewAllAuditProducts(
    {
      dispatch,
      getters: { auditDetails, auditPogRack, auditProducts },
    },
    { $message },
  ) {
    return apollo.defaultClient
      .mutate({
        mutation: ReviewAllAuditProducts,
        variables: {
          expectedCount: auditProducts.length,
          id: auditDetails.id,
          pogsRacksId: auditPogRack.id,
        },
        refetchQueries: [
          {
            query: AuditDetailsQuery,
            variables: {
              id: auditDetails.id,
              requestTime: (state.lastAuditDetailsRequestTime = Date.now()),
            },
          },
        ],
      })
      .then(() => {
        dispatch('loadAuditDetails', { id: +auditDetails.id })
      })
      .catch(err => {
        $message.error({ message: err.message })
      })
  },

  // eslint-disable-next-line
  saveAuditProductDetails(
    {
      dispatch,
      getters: { auditDetails, inMemoryAudit },
    },
    { $message },
  ) {
    // can't edit completed stockchecks
    // if (state.auditDetails.status !== 'In Progress') {
      // $message.error('Can\'t edit an audit that isn\'t In Progress. Changes not saved!')
      // return
    // }

    const list = []

    inMemoryAudit.pogs.filter(pog => pog.image).forEach(pog => {
      let count = 0

      list.push(
        ...pog.products
          .filter(
            (product, index) =>
              product.isDirty
                ? // eslint-disable-next-line
                  ++count
                : // pog is dirty and no products are dirty
                  // TODO split out backend methods for saving pog product and crop
                  pog.crop.isDirty &&
                  count === 0 &&
                  index + 1 === pog.products.length &&
                  !(pog.crop.isDirty = false),
          ) // sets pog.crop.isDirty to false
          // sets product.isDirty to false
          .map(
            product =>
              (product.isDirty = false) || {
                coords: product.coords,
                crop: {
                  scale: pog.crop.scale || 1.0,
                  tx: pog.crop.tx || 0.0,
                  ty: pog.crop.ty || 0.0,
                },
                id: product.id,
                issue: product.issue,
                scImagesId: pog.image.id,
                pogsRacksId: pog.id,
              },
          ),
      )
    })

    if (!list.length) return

    state.pendingAuditSaves += 1

    console.log(`auditDetails: saving ${list.length}`)

    // no pending save, go ahead and save
    apollo.defaultClient
      .mutate({
        mutation: SavePogProductCoords,
        variables: { list },
      })
      .then(() => {
        state.pendingAuditSaves -= 1
      })
      .catch(err => {
        state.pendingAuditSaves -= 1
        $message.error(err.message)
      })
  },

  saveNextAuditDate(context, { $message, fields }) {
    return apollo.defaultClient
      .mutate({
        mutation: SaveNextAuditDate,
        variables: { fields }
      })
      .then(() => {
        $message({
          message: 'Next audit date was saved.',
          type: 'success',
        })
      })
      .catch((err) => {
        $message({
          message: err,
          type: 'error',
        })
      })
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/audit/AuditDetails.js